import { Link } from "react-router-dom";
import { Check, ArrowRight, BedDouble, Users, PawPrint, Clock } from "lucide-react";
import { SITE, PHOTOS, CABIN_FEATURES, CABIN_INFO } from "../data/content";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";

export default function Cabins() {
  return (
    <main data-testid="cabins-page">
      <PageHero
        image={PHOTOS.cabinRow}
        eyebrow="Cabin Rentals"
        title="Clean, stocked cabins steps from the dock."
        subtitle="Most cabins sleep up to 8 with a king bedroom, queen bedroom, plus futons in the living area and loft. All cabins are pet-friendly."
      />

      {/* INTRO + FEATURES */}
      <section className="py-24 bg-[var(--ec-cream)]" data-testid="cabins-intro">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ec-lake)] mb-3">
              The Cabin Experience
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--ec-navy)] leading-[1.05]">
              Ready-to-enjoy, comfortable & well-stocked.
            </h2>
            <p className="mt-6 text-[var(--ec-charcoal)]/80 leading-relaxed">
              We provide clean, comfortable, well-stocked cabins ready to go. Dishes, silverware, cookware, bedding and a grill are included, and parking is close by. Cabins can sleep an average of up to 8 people comfortably — combined, that's roughly 32 guests across all cabins for a group event.
            </p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {CABIN_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[var(--ec-navy)]" data-testid={`feature-${f.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  <Check className="w-5 h-5 text-[var(--ec-lake)] mt-0.5" />
                  <span className="text-sm font-medium">{f}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={150}>
            <div className="grid grid-cols-2 gap-4">
              <div className="img-zoom rounded-2xl overflow-hidden shadow-md card-lift aspect-[4/5]">
                <img src={PHOTOS.cabinDeck} alt="Cabin deck overlooking the lake" className="w-full h-full object-cover" />
              </div>
              <div className="img-zoom rounded-2xl overflow-hidden shadow-md card-lift aspect-[4/5] mt-10">
                <img src={PHOTOS.cabinDeckLong} alt="Long cabin deck with chairs" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RATES */}
      <section className="py-24 bg-white" data-testid="cabins-rates">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeading
            eyebrow="Rates"
            title="Simple nightly rates."
            subtitle="Weekends are a 2-day minimum on regular weekends and a 3-day minimum for holiday weekends (4th of July, Memorial, Labor Day)."
          />
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {CABIN_INFO.rates.map((r, i) => (
              <Reveal key={r.label} delay={i * 100}>
                <div
                  className="rounded-2xl border border-[var(--ec-sand)] bg-[var(--ec-cream)] p-8 card-lift"
                  data-testid={`rate-card-${i}`}
                >
                  <div className="text-xs uppercase tracking-[0.22em] text-[var(--ec-lake)] font-semibold">
                    {r.label}
                  </div>
                  <div className="font-display text-4xl sm:text-5xl text-[var(--ec-navy)] mt-3">
                    {r.price}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 max-w-3xl mx-auto text-center text-sm text-[var(--ec-charcoal)]/80 leading-relaxed" data-testid="cabins-rate-disclosure">
            Rates are per night. Applicable taxes may be added. We'll confirm your full total, deposit and cancellation terms with you before your booking is final.
          </p>
        </div>
      </section>

      {/* LAYOUT/SLEEPING + POLICIES */}
      <section className="py-24 bg-[var(--ec-sand-soft)]" data-testid="cabins-details">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="bg-white rounded-3xl p-8 lg:p-10 border border-[var(--ec-sand)] h-full">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-full bg-[var(--ec-lake)]/10 flex items-center justify-center">
                  <BedDouble className="w-5 h-5 text-[var(--ec-lake)]" />
                </div>
                <h3 className="font-display text-2xl text-[var(--ec-navy)]">Layout & Sleeping</h3>
              </div>
              <ul className="space-y-3">
                {CABIN_INFO.sleeping.map((s) => (
                  <li key={s} className="flex items-start gap-2 text-[var(--ec-charcoal)]/85 text-sm leading-relaxed">
                    <Users className="w-4 h-4 text-[var(--ec-lake)] mt-0.5 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[var(--ec-navy)]">
                <PawPrint className="w-4 h-4 text-[var(--ec-lake)]" />
                All cabins are pet-friendly — let us know in advance.
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="bg-white rounded-3xl p-8 lg:p-10 border border-[var(--ec-sand)] h-full">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-full bg-[var(--ec-lake)]/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[var(--ec-lake)]" />
                </div>
                <h3 className="font-display text-2xl text-[var(--ec-navy)]">Policies</h3>
              </div>
              <ul className="space-y-3">
                {CABIN_INFO.policies.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-[var(--ec-charcoal)]/85 text-sm leading-relaxed">
                    <Check className="w-4 h-4 text-[var(--ec-lake)] mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[var(--ec-navy)] text-white" data-testid="cabins-cta">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center">
          <h2 className="font-display text-4xl sm:text-5xl text-white leading-[1.05]">
            Got a date in mind?
          </h2>
          <p className="mt-5 text-white/75 max-w-xl mx-auto">
            Cabins go fast on weekends. Call us at {SITE.phoneDisplay} — if we don't pick up right away, leave a message and we'll get right back to you.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/contact"
              data-testid="cabins-book-cta"
              className="inline-flex items-center gap-2 bg-[var(--ec-lake)] hover:bg-[var(--ec-lake-dark)] text-white font-semibold px-7 py-4 rounded-full btn-lift"
            >
              Book a Cabin <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="cabins-contact-cta"
              className="inline-flex items-center gap-2 border border-white/50 text-white hover:bg-white/10 font-semibold px-7 py-4 rounded-full btn-lift"
            >
              Send a Message
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
